import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { inputCls as inp } from "@/components/tournament/constants";

const clean = (html) => DOMPurify.sanitize(html || "", { ADD_ATTR: ["target"], FORBID_TAGS: ["style", "script", "iframe", "form"] });

function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/contact", f); setDone(true); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Please check the form"); }
    finally { setBusy(false); }
  };
  if (done) return <div className="card-surface rounded-2xl p-8 text-center" data-testid="contact-done"><CheckCircle2 className="mx-auto text-green-400" size={36} /><div className="mt-3 font-bold text-white">Message sent</div><p className="text-sm text-white/50">We'll get back to you soon.</p></div>;
  return (
    <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-3" data-testid="contact-form">
      <h3 className="font-bold text-white text-lg">Send us a message</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required maxLength={80} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="contact-name" />
        <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="contact-email" />
      </div>
      <input maxLength={150} placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={inp} data-testid="contact-subject" />
      <textarea required minLength={5} maxLength={5000} rows={5} placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inp} data-testid="contact-message" />
      <button disabled={busy} data-testid="contact-submit" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send</button>
    </form>
  );
}

const slugify = (t) => t.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Toc({ items }) {
  if (items.length < 2) return null;
  return (
    <nav className="mb-8 flex flex-wrap gap-2" data-testid="page-toc">
      {items.map((h) => (
        <a key={h.id} href={`#${h.id}`} data-testid={`toc-${h.id}`} className="px-3 py-1.5 rounded-full border border-white/15 text-sm text-white/70 hover:text-white hover:border-[hsl(var(--primary))] transition-colors">{h.text}</a>
      ))}
    </nav>
  );
}

export default function StaticPage({ slug }) {
  const [p, setP] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); setP(null); api.get(`/pages/${slug}`).then((r) => setP(r.data)).catch(() => setP({ title: "Page", content: "" })); }, [slug]);
  const { html, toc } = useMemo(() => {
    if (!p?.content) return { html: "", toc: [] };
    const doc = new DOMParser().parseFromString(clean(p.content), "text/html");
    const hs = [...doc.body.querySelectorAll("h2, h3")];
    hs.forEach((h) => { h.id = h.id || slugify(h.textContent); h.classList.add("scroll-mt-24"); });
    return { html: doc.body.innerHTML, toc: hs.filter((h) => h.tagName === "H2").map((h) => ({ id: h.id, text: h.textContent })) };
  }, [p]);
  useEffect(() => {
    if (!html || !window.location.hash) return;
    const t = setTimeout(() => document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [html]);
  if (!p) return <div className="py-24 text-center text-white/40">Loading…</div>;
  return (
    <div data-testid={`page-${slug}`}>
      <div className="relative h-[40vh] min-h-[280px] overflow-hidden bg-[hsl(var(--card))]">
        {p.image && <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.4 }} src={mediaUrl(p.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-black/60 to-black/30" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 h-full mx-auto max-w-4xl px-4 md:px-6 flex flex-col justify-end pb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="page-title">{p.title}</h1>
          {p.subtitle && <p className="mt-3 text-white/70 max-w-2xl">{p.subtitle}</p>}
        </motion.div>
      </div>
      <div className={`mx-auto max-w-4xl px-4 md:px-6 py-10 ${slug === "contact" ? "grid lg:grid-cols-2 gap-10 max-w-6xl" : ""}`}>
        {slug === "help" && <Toc items={toc} />}
        {p.content ? <div className="article-content" data-testid="page-content" dangerouslySetInnerHTML={{ __html: html }} /> : <p className="text-white/40">Content coming soon.</p>}
        {slug === "contact" && <ContactForm />}
      </div>
    </div>
  );
}
